import { useState, type FormEvent } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
} from '@mui/material';
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { AuthLayout, authFieldSx } from '@dikonek/ui-common';
import { opsApi, OpsApiError } from '../api/client';
import logo from '../assets/dikonek dark mode.png';

/**
 * Consumes the emailed reset token (from the `?token=` query param) and sets a
 * new password. Validates the two fields match and meet the 8-char minimum
 * before hitting the backend; the backend is the source of truth on token
 * validity/expiry.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await opsApi.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof OpsApiError && err.serverMessage
          ? err.serverMessage
          : 'Something went wrong. Please request a new reset link.',
      );
    } finally {
      setBusy(false);
    }
  };

  const backToSignIn = (
    <Link
      component={RouterLink}
      to="/login"
      variant="body2"
      sx={{
        textAlign: 'center',
        color: 'rgba(255,255,255,0.45)',
        textDecorationColor: 'rgba(255,255,255,0.2)',
        '&:hover': { color: 'rgba(255,255,255,0.7)' },
      }}
    >
      Back to sign in
    </Link>
  );

  return (
    <AuthLayout
      appName="Choose a new password"
      subtitle="Set a new password for your account."
      logo={logo}
      logoHeight={120}
    >
      {!token ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Alert
            severity="error"
            variant="outlined"
            sx={{
              borderRadius: 2,
              color: '#ff8a80',
              borderColor: 'rgba(244,67,54,0.4)',
              backgroundColor: 'rgba(244,67,54,0.08)',
            }}
          >
            This reset link is invalid or incomplete. Please request a new one.
          </Alert>
          <Link
            component={RouterLink}
            to="/forgot-password"
            variant="body2"
            sx={{
              textAlign: 'center',
              color: 'rgba(255,255,255,0.45)',
              '&:hover': { color: 'rgba(255,255,255,0.7)' },
            }}
          >
            Request a new reset link
          </Link>
        </Box>
      ) : done ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, textAlign: 'center' }}>
          <Alert
            severity="success"
            variant="outlined"
            sx={{
              borderRadius: 2,
              color: '#a5d6a7',
              borderColor: 'rgba(76,175,80,0.4)',
              backgroundColor: 'rgba(76,175,80,0.08)',
              textAlign: 'left',
            }}
          >
            Your password has been reset. You can now sign in with your new password.
          </Alert>
          {backToSignIn}
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
        >
          {error && (
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                borderRadius: 2,
                color: '#ff8a80',
                borderColor: 'rgba(244,67,54,0.4)',
                backgroundColor: 'rgba(244,67,54,0.08)',
              }}
            >
              {error}
            </Alert>
          )}
          <TextField
            label="New password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: 'rgba(255,255,255,0.4)' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={authFieldSx}
          />
          <TextField
            label="Confirm new password"
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={authFieldSx}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={busy}
            startIcon={busy ? <CircularProgress size={18} /> : undefined}
            sx={{
              mt: 1,
              py: 1.4,
              fontSize: 15,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1565c0 0%, #5e92f3 100%)',
              boxShadow: '0 4px 20px rgba(21,101,192,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #003c8f 0%, #1565c0 100%)',
                boxShadow: '0 6px 24px rgba(21,101,192,0.5)',
              },
              '&.Mui-disabled': {
                background: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            {busy ? 'Resetting...' : 'Reset password'}
          </Button>
          {backToSignIn}
        </Box>
      )}
    </AuthLayout>
  );
}
